import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@material-ui/icons';
import api from "../../services/api";
import {
  buildBusinessConnectPayload,
  isValidOAuthState,
  parseOAuthState,
} from "../../utils/whatsappCloudSignup";
import {
  buildInstagramConnectPayload,
  isValidInstagramOAuthState,
  parseInstagramOAuthState,
} from "../../utils/instagramPageOAuth";
import {
  buildMessengerConnectPayload,
  isValidMessengerOAuthState,
  parseMessengerOAuthState,
} from "../../utils/messengerPageOAuth";

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: 'center',
    maxWidth: 500,
    width: '100%'
  },
  iconContainer: {
    marginBottom: theme.spacing(2)
  },
  successIcon: {
    fontSize: 64,
    color: theme.palette.success.main
  },
  warningIcon: {
    fontSize: 64,
    color: theme.palette.warning.main
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main
  },
  loadingIcon: {
    marginBottom: theme.spacing(2)
  },
  progressBar: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  progressBarContainer: {
    width: '100%',
    backgroundColor: '#e0e0e0',
    borderRadius: 4,
    height: 8,
    overflow: 'hidden',
    marginBottom: theme.spacing(1)
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.3s ease-in-out',
    backgroundColor: theme.palette.primary.main
  },
  stepText: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
    color: theme.palette.text.primary
  },
  button: {
    marginTop: theme.spacing(2)
  },
  diagnosticInfo: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    textAlign: 'left'
  }
}));

const FacebookCallback = () => {
  const classes = useStyles();
  const history = useHistory();
  
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);
  const [nextSteps, setNextSteps] = useState([]);

  // Comunicar resultados a la ventana padre
  const communicateResult = (resultData) => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(resultData, window.location.origin);
      setTimeout(() => {
        if (!window.closed) {
          window.close();
        }
      }, resultData.type === 'FACEBOOK_AUTH_SUCCESS' ? 3000 : 6000);
    } else {
      setTimeout(() => {
        history.push('/connections');
      }, resultData.type === 'FACEBOOK_AUTH_SUCCESS' ? 3000 : 6000);
    }
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setProgress(10);
        setCurrentStep('Validando autorización...');
        setMessage('Procesando autorización de Facebook...');

        // Obtener parámetros de la URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        const state = urlParams.get('state');

        // Validar errores de OAuth
        if (error) {
          setStatus('error');
          setProgress(0);
          setCurrentStep('Error de autorización');
          
          let errorMsg = 'Error de autorización de Facebook';
          if (error === 'access_denied') {
            errorMsg = 'Cancelaste el proceso de autorización. Necesitas aprobar los permisos para conectar WhatsApp Business.';
          } else if (errorDescription) {
            errorMsg = errorDescription;
          }
          
          setMessage(errorMsg);
          
          communicateResult({
            type: 'FACEBOOK_AUTH_ERROR',
            error: error,
            errorDescription: errorMsg,
            timestamp: new Date().toISOString()
          });
          
          return;
        }

        // Validar código
        if (!code) {
          setStatus('error');
          setProgress(0);
          setCurrentStep('Error');
          setMessage('No se recibió el código de autorización de Facebook');
          return;
        }

        const isInstagram = isValidInstagramOAuthState(state);
        const isMessenger = isValidMessengerOAuthState(state);
        const isWhatsapp = isValidOAuthState(state);
        if (!isInstagram && !isMessenger && !isWhatsapp) {
          setStatus('error');
          setProgress(0);
          setCurrentStep('Error de seguridad');
          setMessage('Estado de autorización inválido. Posible intento de CSRF.');
          return;
        }

        const reconnectWhatsappId = isInstagram
          ? parseInstagramOAuthState(state)?.whatsappId
          : isMessenger
            ? parseMessengerOAuthState(state)?.whatsappId
            : parseOAuthState(state)?.whatsappId;

        console.log('✅ Código de autorización recibido');

        setProgress(30);
        setCurrentStep('Procesando autorización...');
        setMessage('Enviando código de autorización al servidor...');

        const connectPath = isInstagram
          ? '/whatsapp/instagram'
          : isMessenger
            ? '/whatsapp/messenger'
            : '/whatsapp/business';
        const connectPayload = isInstagram
          ? buildInstagramConnectPayload({ code, whatsappId: reconnectWhatsappId })
          : isMessenger
            ? buildMessengerConnectPayload({ code, whatsappId: reconnectWhatsappId })
            : buildBusinessConnectPayload({ code, whatsappId: reconnectWhatsappId });

        const response = await api.post(connectPath, connectPayload, { timeout: 30000 });

        // Procesar respuesta del backend
        setProgress(80);
        setCurrentStep('Analizando respuesta...');

        if (response.data) {
          const {
            success,
            whatsappBusinessConfigured,
            messengerConfigured,
            instagramConfigured,
            authorizationStatus,
            message: backendMessage,
            connections,
            count,
            primaryIssue,
            diagnostic,
            suggestions,
            nextSteps: backendNextSteps,
            fallbackOptions,
          } = response.data;

          const configured = isInstagram
            ? instagramConfigured
            : isMessenger
              ? messengerConfigured
              : whatsappBusinessConfigured;
          const flow = isInstagram ? 'instagram' : isMessenger ? 'messenger' : 'whatsapp';

          if (success && configured && connections && connections.length > 0) {
            setProgress(100);
            setStatus('success');
            setCurrentStep('¡Completado!');
            setMessage(
              isInstagram
                ? `¡Conexión exitosa! ${count} cuenta de Instagram configurada.`
                : isMessenger
                  ? `¡Conexión exitosa! ${count} Page de Messenger configurada.`
                  : `¡Conexión exitosa! ${count} número${count > 1 ? 's' : ''} de WhatsApp Business configurado${count > 1 ? 's' : ''}.`,
            );

            communicateResult({
              type: 'FACEBOOK_AUTH_SUCCESS',
              flow,
              connections,
              count,
              timestamp: new Date().toISOString(),
            });

            return;
          }

          if (success && authorizationStatus === 'successful' && !configured) {
            setProgress(90);
            setStatus('warning');
            setCurrentStep('Configuración incompleta');
            setMessage(backendMessage || 'Autorización exitosa, pero necesitas configurar WhatsApp Business.');

            // Guardar información diagnóstica para mostrar al usuario
            setDiagnosticInfo(diagnostic);
            setNextSteps(backendNextSteps || suggestions || []);

            communicateResult({
              type: 'FACEBOOK_AUTH_PARTIAL',
              flow,
              authorized: true,
              whatsappConfigured: false,
              primaryIssue: primaryIssue,
              diagnostic: diagnostic,
              suggestions: suggestions,
              nextSteps: backendNextSteps,
              fallbackOptions: fallbackOptions,
              timestamp: new Date().toISOString()
            });

            return;
          }

          // Caso 3: Error en la creación pero con información útil
          if (!success) {
            setProgress(0);
            setStatus('error');
            setCurrentStep('Error');
            setMessage(backendMessage || 'Error al procesar la autorización');

            if (diagnostic) {
              setDiagnosticInfo(diagnostic);
            }

            return;
          }
        }

        // Si llegamos aquí, algo inesperado pasó
        setProgress(0);
        setStatus('error');
        setCurrentStep('Error inesperado');
        setMessage('Respuesta inesperada del servidor');

      } catch (err) {
        console.error('❌ Error procesando callback:', err);

        setProgress(0);
        setStatus('error');
        setCurrentStep('Error de conexión');

        // Mensajes de error específicos
        if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          setMessage('El servidor tardó demasiado en responder. Por favor, intenta nuevamente.');
        } else if (err.response?.status === 400) {
          setMessage(err.response.data?.error || 'Solicitud inválida');
        } else if (err.response?.status === 401) {
          setMessage('Error de autenticación. Por favor, inicia sesión nuevamente.');
        } else if (err.response?.status === 403) {
          setMessage('No tienes permisos suficientes. Verifica la configuración de tu aplicación de Facebook.');
        } else if (err.response?.status === 500) {
          setMessage('Error en el servidor. Por favor, contacta al soporte técnico.');
        } else if (!navigator.onLine) {
          setMessage('No hay conexión a Internet. Verifica tu conexión y vuelve a intentar.');
        } else {
          setMessage('Error al procesar la autorización. Por favor, intenta nuevamente.');
        }

        if (err.response?.data?.diagnostic) {
          setDiagnosticInfo(err.response.data.diagnostic);
        }

        communicateResult({
          type: 'FACEBOOK_AUTH_ERROR',
          error: err.message,
          details: err.response?.data,
          timestamp: new Date().toISOString()
        });
      }
    };

    handleCallback();
  }, [history]);

  const handleRetry = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
    } else {
      history.push('/connections');
    }
  };

  const handleOpenMetaManager = () => {
    window.open('https://business.facebook.com/', '_blank');
  };

  const renderDiagnosticInfo = () => {
    if (!diagnosticInfo) return null;

    return (
      <Box className={classes.diagnosticInfo}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Información de diagnóstico:</strong>
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          • Cuentas de negocio: {diagnosticInfo.totalBusinesses || 0}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          • WhatsApp configurado: {diagnosticInfo.businessesWithWhatsApp || 0}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          • Números encontrados: {diagnosticInfo.totalPhoneNumbers || 0}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          • Números verificados: {diagnosticInfo.verifiedPhoneNumbers || 0}
        </Typography>
      </Box>
    );
  };

  const renderNextSteps = () => {
    if (!nextSteps || nextSteps.length === 0) return null;

    return (
      <Alert severity="info" style={{ marginTop: 16, textAlign: 'left' }}>
        <AlertTitle>Próximos pasos:</AlertTitle>
        <List dense>
          {nextSteps.map((step, index) => (
            <ListItem key={index} disableGutters>
              <ListItemText 
                primary={`${index + 1}. ${step}`}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleOpenMetaManager}
          style={{ marginTop: 8 }}
        >
          Abrir Meta Business Manager
        </Button>
      </Alert>
    );
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <CircularProgress size={64} className={classes.loadingIcon} />
            <Typography variant="h6" gutterBottom>
              {message || 'Procesando autorización...'}
            </Typography>
            {currentStep && (
              <Typography variant="body1" className={classes.stepText}>
                {currentStep}
              </Typography>
            )}
            <Box className={classes.progressBar}>
              <Box className={classes.progressBarContainer}>
                <Box 
                  className={classes.progressBarFill}
                  style={{ width: `${progress}%` }}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                {progress}% completado
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              Por favor espera mientras procesamos tu autorización
            </Typography>
          </>
        );

      case 'success':
        return (
          <>
            <Box className={classes.iconContainer}>
              <CheckCircleIcon className={classes.successIcon} />
            </Box>
            <Typography variant="h5" gutterBottom>
              ¡Éxito!
            </Typography>
            <Typography variant="body1" paragraph>
              {message}
            </Typography>
            <Typography variant="caption" display="block" color="textSecondary">
              {window.opener ? 'Esta ventana se cerrará automáticamente en 3 segundos...' : 'Serás redirigido en 3 segundos...'}
            </Typography>
          </>
        );

      case 'warning':
        return (
          <>
            <Box className={classes.iconContainer}>
              <WarningIcon className={classes.warningIcon} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Autorización exitosa
            </Typography>
            <Typography variant="body1" paragraph>
              {message}
            </Typography>
            
            {renderDiagnosticInfo()}
            {renderNextSteps()}

            <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 16 }}>
              {window.opener ? 'Esta ventana se cerrará automáticamente en 6 segundos...' : 'Serás redirigido en 6 segundos...'}
            </Typography>
          </>
        );

      case 'error':
        return (
          <>
            <Box className={classes.iconContainer}>
              <ErrorIcon className={classes.errorIcon} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Error
            </Typography>
            <Typography variant="body1" paragraph>
              {message}
            </Typography>

            {renderDiagnosticInfo()}

            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={handleRetry}
              fullWidth
            >
              {window.opener ? 'Cerrar ventana' : 'Volver a conexiones'}
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper} elevation={3}>
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default FacebookCallback;